import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';
import React from 'react';
import type {
  CarServiceBlock,
  ServiceBlock,
  ServiceFamilyModel,
} from 'src/models';

import PublicLayout, {
  getPublicLayoutData,
} from 'src/components/layouts/PublicLayout/PublicLayout';
import CMSLayout from 'src/components/shared/CMSLayout';
import MetaNoIndex from 'src/components/shared/Meta/MetaNoIndex';
import Newsletter from 'src/components/shared/Newsletter/Newsletter';
import Services from 'src/components/shared/Services/Services';

import styles from './Index.module.scss';

import { getGarage } from 'src/services/garage/getGarage';
import {
  getGaragePrestationDetails,
  getGarageServices,
  getGarageServicesData,
} from 'src/services/prestation';

import { logAPIError } from 'src/utils/SentryUtils';

export const getServerSideProps = async ({
  params,
}: GetServerSidePropsContext) => {
  const garageSlug = typeof params?.id === 'string' ? params.id : '';
  const serviceSlug = typeof params?.slug === 'string' ? params.slug : '';

  try {
    const garage = await getGarage(garageSlug);
    const [publicLayoutData, prestationDetails, breadcrumb] = await Promise.all(
      [
        getPublicLayoutData({ garageSlug, garage }),
        getGaragePrestationDetails(garageSlug, serviceSlug),
        getGarageServicesData(garageSlug),
      ],
    );

    let carServiceBlocks: CarServiceBlock[] = [];

    try {
      carServiceBlocks = await getGarageServices(garageSlug, true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (!error.response || error.response.status !== 404) {
        logAPIError(error);
      }
    }

    const finalServices = prestationDetails.familyId
      ? carServiceBlocks.filter(
          (service) => service.id !== prestationDetails.familyId.toString(),
        )
      : carServiceBlocks;

    return {
      props: {
        prestationDetails,
        publicLayoutData,
        breadcrumb,
        carServiceBlocks: garage.hasDevis ? finalServices : [],
        garage,
      },
    };
  } catch (error) {
    logAPIError(error);
    return {
      props: {},
      notFound: true,
    };
  }
};

const GaragePrestationPage: FunctionComponent<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
  publicLayoutData,
  prestationDetails,
  breadcrumb,
  garage,
  carServiceBlocks,
}) => {
  const router = useRouter();
  if (
    router.isFallback ||
    !publicLayoutData ||
    !prestationDetails ||
    !breadcrumb
  ) {
    return null;
  }

  // TODO : to be removed when "cms/car-services/[slug]" gets updated
  const family =
    prestationDetails.family !== undefined
      ? {
          title: prestationDetails.family?.name,
          description: prestationDetails.family?.description,
          pictureLink: prestationDetails.family?.pictureLink,
          alt: prestationDetails.family?.pictureAlt,
          price: parseFloat(prestationDetails.family?.price),
          buttonLabel: prestationDetails.family?.buttonLabel,
          idFamilleIdg: parseInt(prestationDetails.family?.id),
        }
      : undefined;

  return (
    <>
      <MetaNoIndex />
      <PublicLayout data={publicLayoutData}>
        <CMSLayout
          hasNewsLetter={false}
          garage={garage}
          metaDescription={prestationDetails.metaDescription}
          metaTitle={prestationDetails.metaTitle}
          content={prestationDetails.description}
          title={prestationDetails.label}
          serviceOffer={family as ServiceBlock}
          idFamille={`${prestationDetails.family?.id}`}
          averageRating={publicLayoutData.footerData.feedbackStats.reviewNote}
          servicesFamilies={[family as ServiceFamilyModel]}
          totalRatings={publicLayoutData.footerData.feedbackStats.countReview}
          breadcrumbs={[
            ...breadcrumb.breadcrumb,
            ...(breadcrumb.hasCarServicesPage
              ? [
                  {
                    title: 'Prestations',
                    targetUrl: `/garage/${breadcrumb.subDomain}/prestation`,
                  },
                ]
              : []),
            {
              title: prestationDetails.label,
              targetUrl: null,
            },
          ]}
        ></CMSLayout>
        {carServiceBlocks.length > 0 ? (
          <Services
            carServiceBlocks={carServiceBlocks}
            subTitle={''}
            title={'toutes les prestations pour l’entretien de votre auto'}
            hasMoreOffers={false}
            titleLevel={'h3'}
            serviceTitleLevel={'h4'}
            garage={garage}
            hasReadMore
            className={styles.servicesSection}
          />
        ) : (
          <></>
        )}
        <Newsletter />
      </PublicLayout>
    </>
  );
};

export default GaragePrestationPage;
